import React, { useEffect, useState, useMemo } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    TextField,
    Paper,
    Button,
    IconButton,
    Switch,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography
} from '@mui/material';
import api from '../services/api';
import { useSnackbar } from '../context/SnackbarContext';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import TenantRegister from './TenantRegister';
import TenantDetails from './TenantDetails';
import TenantEdit from './TenantEdit';
import { ICliente, IEndereco, IContato, IProduto } from '../interfaces';
import { Chip, Stack } from '@mui/material';

const emptyCliente: ICliente = {
    razaoSocial: '',
    nomeFantasia: '',
    CNPJ: '',
    email: '',
    telefone: '',
    ativo: true,
    endereco: {
        CEP: '',
        logradouro: '',
        numero: '',
        complemento: '',
        bairro: '',
        municipio: '',
        UF: ''
    },
    contato: {
        nome: '',
        cpf: '',
        estadoCivil: '',
        telefone: {
            ddd: '',
            numero: '',
            whatsapp: false
        },
        cargoFuncao: '',
        responsavelTecnico: false,
        responsavelFinanceiro: false
    }
};

const TenantList: React.FC = () => {
    const [clientes, setClientes] = useState<ICliente[]>([]);
    const [filteredClientes, setFilteredClientes] = useState<ICliente[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [formCliente, setFormCliente] = useState<ICliente>(emptyCliente);
    const [products, setProducts] = useState<IProduto[]>([]);
    const [newDialogOpen, setNewDialogOpen] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedTenantId, setSelectedTenantId] = useState<string | null>(null);
    const { exibirMensagem } = useSnackbar();

    useEffect(() => {
        const fetchClientes = async () => {
            try {
                const response = await api.get('api/tenants/list');
                setClientes(response.data);
                setFilteredClientes(response.data);
            } catch (error) {
                exibirMensagem('Erro ao carregar clientes.', 'error');
            }
        };
        fetchClientes();
    }, [exibirMensagem]);

    useEffect(() => {
        const fetchProducts = async () => {
            try {
                const response = await api.get('/api/products');
                setProducts(response.data);
            } catch (error) {
                console.error('Erro ao carregar produtos:', error);
            }
        };
        fetchProducts();
    }, []);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            const filtered = clientes.filter((cliente) =>
                (cliente.razaoSocial || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                (cliente.nomeFantasia || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                (cliente.CNPJ || '').includes(searchTerm)
            );
            setFilteredClientes(filtered);
        }, 300);
        return () => clearTimeout(timeoutId);
    }, [searchTerm, clientes]);

    const handleEnderecoChange = (field: keyof IEndereco, value: string) => {
        setFormCliente({
            ...formCliente,
            endereco: {
                ...formCliente.endereco,
                [field]: value
            }
        });
    };

    const handleContatoChange = (field: keyof IContato, value: any) => {
        setFormCliente({
            ...formCliente,
            contato: {
                ...formCliente.contato,
                [field]: value
            }
        });
    };

    const handleContatoTelefoneChange = (field: keyof IContato['telefone'], value: any) => {
        setFormCliente({
            ...formCliente,
            contato: {
                ...formCliente.contato,
                telefone: {
                    ...formCliente.contato.telefone,
                    [field]: value
                }
            }
        });
    };

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(event.target.value);
    const handleChangePage = (event: unknown, newPage: number) => setPage(newPage);
    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleToggleStatus = async (tenantId: string, ativo: boolean) => {
        try {
            await api.patch(`/api/tenants/${tenantId}/status`, { ativo });
            exibirMensagem('Status atualizado com sucesso!', 'success');
            const response = await api.get('api/tenants/list');
            setClientes(response.data);
            setFilteredClientes(response.data);
        } catch (error) {
            exibirMensagem('Erro ao atualizar status.', 'error');
        }
    };

    const handleOpenNewDialog = () => {
        setFormCliente(emptyCliente);
        setNewDialogOpen(true);
    };

    const handleCloseNewDialog = () => {
        setNewDialogOpen(false);
        setFormCliente(emptyCliente);
    };

    const handleOpenEditDialog = (cliente: ICliente) => {
        setFormCliente(cliente);
        setEditDialogOpen(true);
    };

    const handleCloseEditDialog = () => {
        setEditDialogOpen(false);
        setFormCliente(emptyCliente);
    };

    const handleOpenDetailsDialog = (cliente: ICliente) => {
        setFormCliente(cliente);
        setDetailsDialogOpen(true);
    };

    const handleCloseDetailsDialog = () => {
        setDetailsDialogOpen(false);
        setFormCliente(emptyCliente);
    };

    const handleOpenDeleteDialog = (tenantId: string) => {
        setSelectedTenantId(tenantId);
        setDeleteDialogOpen(true);
    };

    const handleCloseDeleteDialog = () => {
        setSelectedTenantId(null);
        setDeleteDialogOpen(false);
    };

    const handleDeleteTenant = async () => {
        if (!selectedTenantId) return;

        try {
            await api.delete(`/api/tenants/${selectedTenantId}`);
            exibirMensagem('Cliente excluído com sucesso!', 'success');
            const response = await api.get('api/tenants/list');
            setClientes(response.data);
            setFilteredClientes(response.data);
        } catch (error) {
            exibirMensagem('Erro ao excluir cliente.', 'error');
        } finally {
            handleCloseDeleteDialog();
        }
    };

    return (
        <div className="clientes-container">
            <TextField
                label="Buscar clientes"
                variant="outlined"
                fullWidth
                margin="normal"
                value={searchTerm}
                onChange={handleSearchChange}
            />
            <Button variant="contained" onClick={handleOpenNewDialog} sx={{ mb: 2 }}>
                NOVO CLIENTE
            </Button>
            <Paper>
                <TableContainer sx={{ marginTop: '20px' }}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell>Razão Social</TableCell>
                                <TableCell>Nome Fantasia</TableCell>
                                <TableCell>CNPJ</TableCell>
                                <TableCell>Produtos</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Tenant Id</TableCell>
                                <TableCell>Ações</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredClientes
                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                .map((cliente) => (
                                    <TableRow key={cliente._id}>
                                        <TableCell>{cliente.razaoSocial}</TableCell>
                                        <TableCell>{cliente.nomeFantasia}</TableCell>
                                        <TableCell>{cliente.CNPJ}</TableCell>
                                        <TableCell>
                                            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                                                {(((cliente as any).products) || []).map((pid: string) => {
                                                    const prod = products.find((p) => p._id === pid);
                                                    const label = prod ? prod.name : pid;
                                                    const palette = ['#1976d2', '#388e3c', '#f57c00', '#d32f2f', '#7b1fa2', '#0097a7', '#c2185b', '#616161'];
                                                    const idx = products.findIndex((p) => p._id === pid);
                                                    const color = idx >= 0 ? palette[idx % palette.length] : '#9e9e9e';
                                                    return (
                                                        <Chip key={pid} label={label} size="small" sx={{ backgroundColor: color, color: '#fff' }} />
                                                    );
                                                })}
                                            </Stack>
                                        </TableCell>
                                        <TableCell>
                                            <Switch
                                                checked={cliente.ativo}
                                                onChange={() => handleToggleStatus(cliente.tenantId || '', !cliente.ativo)}
                                            />
                                        </TableCell>
                                        <TableCell>{cliente.tenantId}</TableCell>
                                        <TableCell>
                                            <IconButton onClick={() => handleOpenDetailsDialog(cliente)}>
                                                <VisibilityIcon />
                                            </IconButton>
                                            <IconButton onClick={() => handleOpenEditDialog(cliente)}>
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton onClick={() => handleOpenDeleteDialog(cliente.tenantId || '')}>
                                                <DeleteIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={filteredClientes.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>

            <TenantRegister
                open={newDialogOpen}
                onClose={handleCloseNewDialog}
                onSave={() => { }}
                formCliente={formCliente}
                setFormCliente={setFormCliente}
                handleEnderecoChange={() => { }}
                handleContatoChange={() => { }}
                handleContatoTelefoneChange={() => { }}
            />
            <TenantEdit
                open={editDialogOpen}
                onClose={handleCloseEditDialog}
                formCliente={formCliente}
                setFormCliente={setFormCliente}
                updateTenantList={async () => {
                    const response = await api.get('api/tenants/list');
                    setClientes(response.data);
                    setFilteredClientes(response.data);
                }}
                handleEnderecoChange={handleEnderecoChange}
                handleContatoChange={handleContatoChange}
                handleContatoTelefoneChange={handleContatoTelefoneChange}
            />

            <TenantDetails
                open={detailsDialogOpen}
                onClose={handleCloseDetailsDialog}
                cliente={formCliente}
            />

            <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
                <DialogTitle>Confirmar Exclusão</DialogTitle>
                <DialogContent>
                    <Typography>Tem certeza de que deseja excluir este cliente?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDeleteDialog}>Cancelar</Button>
                    <Button onClick={handleDeleteTenant} variant="contained" color="error">
                        Excluir
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default TenantList;